import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Form, ProgressBar } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function BrandQuiz() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('reminder'); // reminder, quiz, calculating
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Context data
  const [context, setContext] = useState({});

  // Quiz state
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [responses, setResponses] = useState({
    1: '', 2: '', 3: '', 4: '', 5: '', 6: ''
  });

  useEffect(() => {
    loadContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContext = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch('/api/brand/get-context', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setContext(data);

        // Load any existing quiz responses
        const responsesRes = await fetch('/api/brand/get-quiz-responses', {
          credentials: 'include'
        });

        if (responsesRes.ok) {
          const responsesData = await responsesRes.json();
          if (responsesData.responses && responsesData.responses.length > 0) {
            const existingResponses = {};
            responsesData.responses.forEach(r => {
              existingResponses[r.question_number] = r.response;
            });
            setResponses(prev => ({ ...prev, ...existingResponses }));
          }
        }
      } else {
        setError('Failed to load context data');
      }
    } catch (err) {
      setError('Error loading data: ' + err.message);
    }

    setLoading(false);
  };

  const handleResponseChange = (e) => {
    setResponses({
      ...responses,
      [currentQuestion]: e.target.value
    });
    setError('');
  };

  const saveCurrentResponse = async () => {
    const responseText = responses[currentQuestion].trim();

    if (!responseText) {
      setError('Please enter your answer before continuing');
      return false;
    }

    try {
      const response = await fetch('/api/brand/save-quiz-response', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question_number: currentQuestion,
          response: responseText
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save response');
        return false;
      }

      return true;
    } catch (err) {
      setError('Error saving response: ' + err.message);
      return false;
    }
  };

  const handleNext = async () => {
    const saved = await saveCurrentResponse();
    if (!saved) return;

    if (currentQuestion < 6) {
      setCurrentQuestion(currentQuestion + 1);
      setError('');
    } else {
      // All questions answered - calculate archetypes
      calculateArchetypes();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
      setError('');
    }
  };

  const calculateArchetypes = async () => {
    setStage('calculating');
    setError('');

    try {
      const response = await fetch('/api/brand/calculate-archetypes', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Navigate to results page (we'll create this next)
        navigate('/dashboard/brand-results', { state: { archetypeData: data } });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to calculate archetypes');
        setStage('quiz');
      }
    } catch (err) {
      setError('Error calculating archetypes: ' + err.message);
      setStage('quiz');
    }
  };

  const startQuestions = () => {
    setStage('quiz');
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  // Reminder Screen
  if (stage === 'reminder') {
    return (
      <Container className="py-5">
        <BackToDashboard module="branding" />
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-5">
                <h2 className="mb-4">📋 Before We Begin...</h2>

                <p className="lead mb-4">
                  Let's refresh what you've already defined:
                </p>

                {context.mission_statement && (
                  <div className="mb-4">
                    <h5>Your Mission:</h5>
                    <p className="text-muted">"{context.mission_statement}"</p>
                  </div>
                )}

                {context.vision_statement && (
                  <div className="mb-4">
                    <h5>Your Vision:</h5>
                    <p className="text-muted">"{context.vision_statement}"</p>
                  </div>
                )}

                {context.why_statement && (
                  <div className="mb-4">
                    <h5>Your Why:</h5>
                    <p className="text-muted">"{context.why_statement}"</p>
                  </div>
                )}

                {context.core_values && context.core_values.length > 0 && (
                  <div className="mb-4">
                    <h5>Your Core Values:</h5>
                    <ul>
                      {context.core_values.map((value, idx) => (
                        <li key={idx}>{value}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <hr className="my-4" />

                <Alert variant="info">
                  <strong>Keep these in mind</strong> as you answer the following questions. Your brand archetype should align with your mission, vision, and values.
                </Alert>

                <p className="mb-4">
                  The questions are designed to be intuitive - answer authentically and you can combine choices naturally:
                </p>

                <ul className="mb-4">
                  <li>"A"</li>
                  <li>"A with some C"</li>
                  <li>"Mostly B with a touch of E"</li>
                </ul>

                <Alert variant="success">
                  <strong>There are no wrong answers.</strong> Just be true to your brand.
                </Alert>

                <div className="d-flex justify-content-between mt-5">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/brand-quiz-intro')}
                  >
                    ← Back
                  </Button>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={startQuestions}
                  >
                    Start Questions →
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Calculating Screen
  if (stage === 'calculating') {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} className="text-center">
            <Card className="shadow">
              <Card.Body className="p-5">
                <div className="mb-4">
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
                <h4>Analyzing Your Brand Archetype...</h4>
                <p className="text-muted">Calculating scores across all 12 archetypes</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Quiz Questions
  const question = getQuestion(currentQuestion);
  const progress = ((currentQuestion - 1) / 6) * 100;

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-4">
              <div className="mb-4">
                <h5 className="text-muted mb-2">QUESTION {currentQuestion} OF 6</h5>
                <ProgressBar now={progress} className="mb-3" style={{ height: '8px' }} />
              </div>

              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              <h4 className="mb-4">{question.text}</h4>

              <div className="mb-4">
                {question.options.map((option, idx) => (
                  <div key={idx} className="mb-3 p-3 bg-light rounded">
                    <strong>{option.letter}. {option.text}</strong>
                    <p className="text-muted small mb-0 mt-1">
                      <em>Examples: {option.examples}</em>
                    </p>
                  </div>
                ))}
              </div>

              <Form.Group className="mb-4">
                <Form.Label><strong>Your answer:</strong></Form.Label>
                <Form.Control
                  type="text"
                  placeholder='e.g., "A", "A with some C", "Mostly B with a touch of E"'
                  value={responses[currentQuestion]}
                  onChange={handleResponseChange}
                  autoFocus
                />
                <Form.Text className="text-muted">
                  💡 You can combine answers: "A with some C" or "Mostly B"
                </Form.Text>
              </Form.Group>

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={handlePrevious}
                  disabled={currentQuestion === 1}
                >
                  ← Previous
                </Button>
                <Button
                  variant="primary"
                  onClick={handleNext}
                >
                  {currentQuestion === 6 ? 'Finish →' : 'Next →'}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

// Question data
function getQuestion(number) {
  const questions = {
    1: {
      text: "What is the purpose of your business?",
      options: [
        { letter: 'A', text: 'To make the world a better, safer place', examples: 'Dove: "Real beauty" | Whole Foods | TOMS: "One for One"' },
        { letter: 'B', text: 'To seek and share knowledge', examples: 'TED: "Ideas worth spreading" | Google | Khan Academy' },
        { letter: 'C', text: 'To help people experience freedom and discovery', examples: 'Patagonia | REI: "A life outdoors" | Airbnb: "Belong anywhere"' },
        { letter: 'D', text: 'To disrupt the status quo and break rules', examples: 'Virgin: "Screw it, let\'s do it" | Harley-Davidson | Apple: "Think different"' },
        { letter: 'E', text: 'To create wonder and transformation', examples: 'Disney: "Where dreams come true" | Tesla | Apple' },
        { letter: 'F', text: 'To help people rise to challenges and prove their worth', examples: 'Nike: "Just do it" | Red Bull: "Gives you wings" | CrossFit' }
      ]
    },
    2: {
      text: "How do you want customers to feel when they interact with your brand?",
      options: [
        { letter: 'A', text: 'Safe, trusting, and optimistic', examples: 'Johnson & Johnson | Seventh Generation | Aveeno: "Naturally beautiful results"' },
        { letter: 'B', text: 'Informed, enlightened, and confident', examples: 'Harvard Business Review | Coursera | LinkedIn Learning' },
        { letter: 'C', text: 'Free, excited, and adventurous', examples: 'GoPro: "Be a Hero" | The North Face | Lonely Planet' },
        { letter: 'D', text: 'Rebellious, liberated, and empowered', examples: 'Harley-Davidson: "Live by it" | Diesel | Jack Daniel\'s' },
        { letter: 'E', text: 'Amazed, inspired, and transformed', examples: 'Apple: "Think different" | Dyson | SpaceX' },
        { letter: 'F', text: 'Strong, capable, and accomplished', examples: 'Under Armour: "I Will" | Gatorade: "Win from within" | Navy SEALs' }
      ]
    },
    3: {
      text: "How does your brand relate to rules and conventions?",
      options: [
        { letter: 'A', text: 'Embraces traditional values and proven approaches', examples: 'Hallmark | Coca-Cola: "The pause that refreshes" | Campbell\'s' },
        { letter: 'B', text: 'Questions everything and seeks deeper truth', examples: 'The Economist | MIT | National Geographic' },
        { letter: 'C', text: 'Ignores them in pursuit of new experiences', examples: 'Airbnb: "Don\'t go there. Live there" | Uber | WeWork' },
        { letter: 'D', text: 'Actively challenges and subverts them', examples: 'Diesel: "Be Stupid" | Ben & Jerry\'s | Brewdog' },
        { letter: 'E', text: 'Transcends them through innovation', examples: 'Tesla: "The future of driving" | Dyson | SpaceX' },
        { letter: 'F', text: 'Uses them strategically to achieve goals', examples: 'American Express | BMW: "The ultimate driving machine" | Rolex' }
      ]
    },
    4: {
      text: "What transformation do you promise your customers?",
      options: [
        { letter: 'A', text: 'Return to innocence, simplicity, and purity', examples: 'Dove: "Real beauty" | Whole Foods | Burt\'s Bees: "Nature\'s way to beautiful"' },
        { letter: 'B', text: 'Gain wisdom, expertise, and mastery', examples: 'MasterClass | Harvard | McKinsey' },
        { letter: 'C', text: 'Find freedom, authenticity, and self-discovery', examples: 'Jeep: "Go anywhere. Do anything" | REI | Lonely Planet' },
        { letter: 'D', text: 'Break free from limitations and oppression', examples: 'Harley-Davidson | Virgin Atlantic | Converse: "Made by you"' },
        { letter: 'E', text: 'Realize dreams, aspirations, and potential', examples: 'Disney: "Where dreams come true" | Oprah | MasterCard: "Priceless"' },
        { letter: 'F', text: 'Become your best self and achieve greatness', examples: 'Nike: "Find your greatness" | Peloton | U.S. Army: "Be all you can be"' }
      ]
    },
    5: {
      text: "How does your brand communicate?",
      options: [
        { letter: 'A', text: 'Simple, clear, and wholesome', examples: 'Dove | IKEA: "The wonderful everyday" | Innocent Smoothies' },
        { letter: 'B', text: 'Thoughtful, analytical, and authoritative', examples: 'The New York Times | Audi: "Vorsprung durch Technik" | IBM' },
        { letter: 'C', text: 'Adventurous, bold, and unconventional', examples: 'Red Bull: "Gives you wings" | GoPro | The North Face: "Never stop exploring"' },
        { letter: 'D', text: 'Provocative, edgy, and counter-culture', examples: 'Diesel: "Be Stupid" | Cards Against Humanity | Old Spice' },
        { letter: 'E', text: 'Visionary, imaginative, and inspiring', examples: 'Apple: "Think different" | TED: "Ideas worth spreading" | Tesla' },
        { letter: 'F', text: 'Confident, motivating, and action-oriented', examples: 'Nike: "Just do it" | Under Armour: "Rule yourself" | Gatorade' }
      ]
    },
    6: {
      text: "If your brand were a person, they would be:",
      options: [
        { letter: 'A', text: 'Optimistic, innocent, and pure', examples: 'Dove | Disney | Whole Foods: "America\'s healthiest grocery store"' },
        { letter: 'B', text: 'Wise, knowledgeable, and mentor-like', examples: 'Google: "Don\'t be evil" | PBS | The Economist: "What if?"' },
        { letter: 'C', text: 'Independent, adventurous, and pioneering', examples: 'Patagonia: "Don\'t buy this jacket" | Jeep | REI: "#OptOutside"' },
        { letter: 'D', text: 'Rebellious, revolutionary, and disruptive', examples: 'Harley-Davidson | Virgin | Apple (1984): "Why 1984 won\'t be like 1984"' },
        { letter: 'E', text: 'Charismatic, transformative, and visionary', examples: 'Apple | Tesla: "Accelerating sustainable transport" | Oprah' },
        { letter: 'F', text: 'Courageous, strong, and determined', examples: 'Nike | FedEx: "When it absolutely, positively has to be there" | Marines' }
      ]
    }
  };

  return questions[number];
}

export default BrandQuiz;
